import React from 'react';
import { makeStyles, FormControlLabel, Checkbox, Typography, Paper } from '~/lib/bn-material-ui';
import { DEFAULT_MATERIAL_COLOR } from './material-colors';

const useStyles = makeStyles(theme => ({
  container: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 1000,
    minWidth: '120px',
    userSelect: 'none',
  },
  checkboxGroup: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(0.5),
    justifyContent: 'center',
  },
  legend: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  colorSymbol: {
    fontSize: '12px',
    flexShrink: 0,
    lineHeight: 1,
  },
  legendText: {
    fontSize: '0.75rem',
    color: theme.palette.gray[80],
  },
}));

export const HighlightModes = {
  NONE: 'none',
  FILL_LEVEL: 'fill_level',
  MATERIAL: 'material',
};

/**
 * Component for controlling inventory map highlighting
 * @param {Object} props - Component props
 * @param {string} props.highlightMode - Current highlight mode
 * @param {Function} props.onHighlightModeChange - Callback when highlight mode changes
 * @param {Object} props.materialColorMap - Map of material names to colors
 * @param {Array} props.binsForMap - Array of bin data for map
 * @returns {JSX.Element} Highlight controls component
 */
export default function InventoryMapHighlightControls({
  highlightMode = HighlightModes.NONE,
  onHighlightModeChange,
  materialColorMap = {},
  binsForMap = [],
}) {
  const styles = useStyles();

  const handleModeChange = (mode) => {
    if (highlightMode === mode) {
      onHighlightModeChange(HighlightModes.NONE);
    } else {
      onHighlightModeChange(mode);
    }
  };

  // Get unique materials for legend
  const materials = React.useMemo(() => {
    const uniqueMaterials = [...new Set(binsForMap.map(bin => bin.material).filter(Boolean))];
    return uniqueMaterials.sort();
  }, [binsForMap]);

  return (
    <div className={styles.container}>
      <div className={styles.checkboxGroup}>
        <FormControlLabel
          control={
            <Checkbox
              checked={highlightMode === HighlightModes.FILL_LEVEL}
              onChange={() => handleModeChange(HighlightModes.FILL_LEVEL)}
            />
          }
          label='Fill Level'
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={highlightMode === HighlightModes.MATERIAL}
              onChange={() => handleModeChange(HighlightModes.MATERIAL)}
            />
          }
          label='Material'
        />
      </div>

      {highlightMode === HighlightModes.MATERIAL && materials.length > 0 && (
        <Paper className={styles.legend} elevation={2}>
          <Typography variant='caption' style={{ fontWeight: 600  }}>
            Materials
          </Typography>
          {materials.map(material => (
            <div key={material} className={styles.legendItem}>
              <span
                className={styles.colorSymbol}
                style={{ color: materialColorMap[material] || DEFAULT_MATERIAL_COLOR }}
              >
                ⬤
              </span>
              <Typography className={styles.legendText}>
                {material}
              </Typography>
            </div>
          ))}
        </Paper>
      )}
    </div>
  );
}
